import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { User, Mail, Key, CreditCard, LogOut, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { CreditPurchase } from '../components/payments/CreditPurchase';
import { getCreditBalance } from '../lib/api';
import { AuthForm } from '../components/auth/AuthForm';
export function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // Auth state
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // User data
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Credits
  const [credits, setCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);
  useEffect(() => {
    checkAuthStatus();
  }, []);
  const checkAuthStatus = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (user) {
      setIsAuthenticated(true);
      setEmail(user.email || '');
      loadCredits();
    } else {
      const guestMode = localStorage.getItem('guestMode') === 'true';
      setIsGuestMode(guestMode);
    }
  };
  const loadCredits = async () => {
    setLoadingCredits(true);
    try {
      const balance = await getCreditBalance();
      setCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
      setCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match'
      });
      setLoading(false);
      return;
    }
    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 6 characters'
      });
      setLoading(false);
      return;
    }
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      setMessage({
        type: 'success',
        text: 'Password updated successfully'
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update password'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  // Guest mode UI
  if (isGuestMode && !isAuthenticated) {
    return <div className="container py-8 max-w-md" data-id="element-2359">
        <div className="text-center mb-8" data-id="element-2360">
          <h1 className="text-3xl font-bold mb-2" data-id="element-2361">Create Your Account</h1>
          <p className="text-muted-foreground" data-id="element-2362">
            Sign up to get 5 free credits and save your transcriptions
          </p>
        </div>
        <Card data-id="element-2363">
          <CardContent className="p-6" data-id="element-2364">
            <AuthForm onLoginSuccess={() => {
            localStorage.removeItem('guestMode');
            window.location.reload();
          }} data-id="element-2365" />
          </CardContent>
        </Card>
      </div>;
  }
  // Authenticated user UI
  return <div className="container py-8 max-w-4xl" data-id="element-2366">
      <div className="mb-8" data-id="element-2367">
        <h1 className="text-3xl font-bold mb-2" data-id="element-2368">Settings</h1>
        <p className="text-muted-foreground" data-id="element-2369">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-6" data-id="element-2370">
        {/* Message */}
        {message && <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20' : 'bg-destructive/10 border border-destructive/20'}`} data-id="element-2371">
            {message.type === 'success' ? <CheckCircle size={20} className="text-green-500" data-id="element-2372" /> : <AlertCircle size={20} className="text-destructive" data-id="element-2373" />}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`} data-id="element-2374">
              {message.text}
            </p>
          </div>}

        {/* Account Information */}
        <section data-id="element-2375">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2376">
            <User size={20} data-id="element-2377" />
            Account Information
          </h2>
          <Card data-id="element-2378">
            <CardContent className="p-6 space-y-4" data-id="element-2379">
              <div data-id="element-2380">
                <label className="block text-sm font-medium mb-2" data-id="element-2381">
                  Email Address
                </label>
                <div className="flex items-center gap-2" data-id="element-2382">
                  <Mail size={16} className="text-muted-foreground" data-id="element-2383" />
                  <Input type="email" value={email} disabled data-id="element-2384" />
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-id="element-2385">
                  Contact support to change your email address
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Change Password */}
        <section data-id="element-2386">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2387">
            <Key size={20} data-id="element-2388" />
            Change Password
          </h2>
          <Card data-id="element-2389">
            <CardContent className="p-6" data-id="element-2390">
              <form onSubmit={handleUpdatePassword} className="space-y-4" data-id="element-2391">
                <div data-id="element-2392">
                  <label className="block text-sm font-medium mb-2" data-id="element-2393">
                    New Password
                  </label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" required data-id="element-2394" />
                </div>

                <div data-id="element-2395">
                  <label className="block text-sm font-medium mb-2" data-id="element-2396">
                    Confirm New Password
                  </label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required data-id="element-2397" />
                </div>

                <Button type="submit" disabled={loading} data-id="element-2398">
                  {loading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-2399" />
                      Updating...
                    </> : <>
                      <Save size={16} className="mr-2" data-id="element-2400" />
                      Update Password
                    </>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Credits & Billing */}
        <section data-id="element-2401">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2402">
            <CreditCard size={20} data-id="element-2403" />
            Credits & Billing
          </h2>
          <Card data-id="element-2404">
            <CardContent className="p-6" data-id="element-2405">
              {loadingCredits ? <div className="flex items-center justify-center py-8" data-id="element-2406">
                  <Loader2 size={24} className="animate-spin text-primary" data-id="element-2407" />
                </div> : <CreditPurchase currentCredits={credits} onPurchaseComplete={loadCredits} data-id="element-2408" />}
            </CardContent>
          </Card>
        </section>

        {/* Sign Out */}
        <section data-id="element-2409">
          <Card className="border-destructive/20" data-id="element-2410">
            <CardContent className="p-6" data-id="element-2411">
              <h3 className="font-semibold mb-2" data-id="element-2412">Sign Out</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-2413">
                Sign out of your account on this device
              </p>
              <Button variant="destructive" onClick={handleSignOut} data-id="element-2414">
                <LogOut size={16} className="mr-2" data-id="element-2415" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>;
}